import { berlinYearMonth } from './berlinCalendar';

export type WalletPreset = 'months_1' | 'months_2' | 'months_3' | 'year' | 'custom';
/** `from` and `until` are Berlin calendar months as `YYYY-MM`. */
export type WalletPeriod = { preset: WalletPreset; from: string; until: string };

export const DEFAULT_PRESET: Exclude<WalletPreset, 'custom'> = 'months_3';
export const PRESETS: WalletPreset[] = ['months_1', 'months_2', 'months_3', 'year', 'custom'];

const PRESET_MONTHS: Record<Exclude<WalletPreset, 'year' | 'custom'>, number> = {
  months_1: 1,
  months_2: 2,
  months_3: 3
};

/** The API answers 400 for an `until` further ahead than this (`MAX_MONTHS_AHEAD` in its wallet serializer). */
export const MAX_MONTHS_AHEAD = 24;

const pad = (n: number) => String(n).padStart(2, '0');

/** The month `today` falls in on the Berlin calendar, which is the one the API counts in. */
export const currentMonth = (today: Date): string => berlinYearMonth(today.toISOString());

export const berlinYear = (today: Date) => Number(currentMonth(today).slice(0, 4));

export const addMonths = (month: string, n: number): string => {
  const [year, m] = month.split('-').map(Number);
  const index = year * 12 + (m - 1) + n;
  return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`;
};

const maxMonth = (a: string, b: string) => (a > b ? a : b);
const minMonth = (a: string, b: string) => (a < b ? a : b);

export const periodForPreset = (
  preset: Exclude<WalletPreset, 'custom'>,
  today: Date
): WalletPeriod => {
  const from = currentMonth(today);
  const until =
    preset === 'year' ? `${berlinYear(today)}-12` : addMonths(from, PRESET_MONTHS[preset]);
  return { preset, from, until };
};

export const pickFrom = (period: WalletPeriod, from: string, today: Date): WalletPeriod => {
  const clamped = maxMonth(from, currentMonth(today));
  return { preset: 'custom', from: clamped, until: maxMonth(period.until, clamped) };
};

export const pickUntil = (period: WalletPeriod, until: string, today: Date): WalletPeriod => {
  const floor = currentMonth(today);
  const clamped = maxMonth(until, floor);
  const from = period.from > clamped ? clamped : maxMonth(period.from, floor);
  return { preset: 'custom', from, until: clamped };
};

/**
 * Moves a period along when the month changed under an open page: presets are
 * recomputed, a custom range never starts before the current month, and one that
 * ended before the current month falls back to the default preset.
 */
export const periodForToday = (period: WalletPeriod, today: Date): WalletPeriod => {
  if (period.preset !== 'custom') return periodForPreset(period.preset, today);

  const month = currentMonth(today);
  if (period.until < month) return periodForPreset(DEFAULT_PRESET, today);

  const from = maxMonth(period.from, month);
  return from === period.from ? period : { ...period, from };
};

export const walletPeriodStorageKey = (userId: string, companyId: string) =>
  `pxtraCard.wallet.period.${userId}.${companyId}`;

const isPreset = (value: unknown): value is WalletPreset =>
  typeof value === 'string' && (PRESETS as string[]).includes(value);

const isYearMonth = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

export const loadWalletPeriod = (key: string, today: Date): WalletPeriod => {
  const fallback = periodForPreset(DEFAULT_PRESET, today);

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== 'object' || stored === null) return fallback;

    const { preset, from, until } = stored as Record<string, unknown>;
    if (!isPreset(preset)) return fallback;
    if (preset !== 'custom') return periodForPreset(preset, today);
    if (!isYearMonth(from) || !isYearMonth(until)) return fallback;
    if (until < from) return fallback;

    const ceiling = addMonths(currentMonth(today), MAX_MONTHS_AHEAD);
    return periodForToday(
      { preset, from: minMonth(from, ceiling), until: minMonth(until, ceiling) },
      today
    );
  } catch {
    return fallback;
  }
};

export const saveWalletPeriod = (key: string, period: WalletPeriod) => {
  try {
    localStorage.setItem(key, JSON.stringify(period));
  } catch {
    // Private mode or blocked storage: the choice simply does not persist.
  }
};
